import React, { useState, useEffect, startTransition } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import ThemeToggle from './ThemeToggle';
import { getAuthState } from '../utils/auth';

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: ${({ $isScrolled }) => ($isScrolled ? 'rgba(8, 16, 30, 0.9)' : 'rgba(8, 16, 30, 0.78)')};
  backdrop-filter: blur(${({ $isScrolled }) => ($isScrolled ? '18px' : '14px')});
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: ${({ $isScrolled }) => ($isScrolled ? '0 10px 30px rgba(0, 0, 0, 0.16)' : 'none')};
  transition: background 0.3s ease, backdrop-filter 0.3s ease, box-shadow 0.3s ease, padding 0.3s ease, min-height 0.3s ease;
  padding: ${({ $isScrolled }) => ($isScrolled ? '0.7rem 2rem' : '1rem 2rem')};
  min-height: ${({ $isScrolled }) => ($isScrolled ? '72px' : '88px')};
  display: flex;
  align-items: center;

  @media (max-width: 1024px) {
    padding: ${({ $isScrolled }) => ($isScrolled ? '0.7rem 1.5rem' : '0.875rem 1.5rem')};
  }

  @media (max-width: 768px) {
    padding: ${({ $isScrolled }) => ($isScrolled ? '0.65rem 1rem' : '0.75rem 1rem')};
  }

  @media (max-width: 480px) {
    padding: ${({ $isScrolled }) => ($isScrolled ? '0.6rem 0.75rem' : '0.625rem 0.75rem')};
  }
`;

const HeaderContent = styled.div`
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;

  @media (max-width: 768px) {
    position: relative;
    gap: 0.5rem;
  }
`;

const Logo = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
  margin-right: 0.75rem;

  img {
    display: block;
    height: 72px;
    width: auto;
    max-width: none;
    object-fit: contain;
    object-position: center center;
    transform: translateZ(0);
    backface-visibility: hidden;
    image-rendering: auto;
    transition: transform 0.3s ease;
    opacity: 1;
    filter: none;
  }

  &:hover img {
    transform: scale(1.02) translateZ(0);
  }

  @media (max-width: 1024px) {
    img {
      height: 64px;
    }
  }

  @media (max-width: 768px) {
    img {
      height: 54px;
    }
  }

  @media (max-width: 480px) {
    img {
      height: 48px;
    }
  }
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: 1.25rem;
  margin-left: auto;
  flex-wrap: nowrap;

  @media (max-width: 1024px) {
    gap: 1rem;
  }

  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    right: 0;
    width: min(86vw, 320px);
    height: 100vh;
    background: rgba(8, 16, 30, 0.92);
    backdrop-filter: blur(24px);
    flex-direction: column;
    align-items: flex-start;
    gap: 1.25rem;
    padding: 6rem 1.5rem 2rem;
    border-left: 1px solid rgba(255, 255, 255, 0.08);
    box-shadow: -10px 0 30px rgba(0, 0, 0, 0.24);
    overflow-y: auto;
    transform: translateX(100%);
    transition: transform 0.3s ease;

    &.open {
      transform: translateX(0);
    }
  }

  @media (max-width: 480px) {
    width: min(90vw, 320px);
    padding: 5.5rem 1.25rem 2rem;
    gap: 1rem;
  }
`;

const NavLink = styled(motion.button)`
  position: relative;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.88);
  font-weight: 600;
  font-size: 0.95rem;
  letter-spacing: 0.01em;
  cursor: pointer;
  padding: 0.5rem 0.2rem;
  white-space: nowrap;
  transition: color 0.25s ease;

  &::after {
    content: '';
    position: absolute;
    left: 0.2rem;
    bottom: 0.15rem;
    width: 0;
    height: 2px;
    border-radius: 999px;
    background: linear-gradient(90deg, #f59e0b 0%, #fb923c 100%);
    transition: width 0.25s ease;
  }

  &:hover,
  &:focus-visible {
    color: #f59e0b;
    outline: none;
  }

  &:hover::after,
  &:focus-visible::after,
  ${({ $active }) => $active && '::after'} {
    width: calc(100% - 0.4rem);
  }

  ${({ $active }) => $active && `
    color: #f59e0b;
  `}

  @media (max-width: 1024px) {
    font-size: 0.9rem;
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: 0.8rem 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    text-align: left;
    font-size: 1rem;

    &::after {
      left: 0;
      bottom: 0.4rem;
    }
  }
`;

const AuthButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 0.85rem;
  margin-left: 0.75rem;
  flex-wrap: nowrap;

  @media (max-width: 1024px) {
    gap: 0.7rem;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.9rem;
    width: 100%;
    margin-left: 0;
    margin-top: 0.75rem;
    padding-top: 0.75rem;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
  }
`;

const LoginButton = styled(motion.button)`
  padding: 0.68rem 1.2rem;
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s ease;
  white-space: nowrap;

  &:hover,
  &:focus-visible {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.28);
    transform: translateY(-1px);
    outline: none;
  }

  @media (max-width: 1024px) {
    padding: 0.6rem 1rem;
    font-size: 0.92rem;
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: 0.8rem 1rem;
    font-size: 1rem;
  }
`;

const SignupButton = styled(motion.button)`
  padding: 0.68rem 1.2rem;
  background: linear-gradient(135deg, #7c3aed 0%, #6366f1 100%);
  color: white;
  border: none;
  border-radius: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  white-space: nowrap;
  box-shadow: 0 10px 30px rgba(124, 58, 237, 0.28);

  &:hover,
  &:focus-visible {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(124, 58, 237, 0.35);
    outline: none;
  }

  @media (max-width: 1024px) {
    padding: 0.6rem 1rem;
    font-size: 0.92rem;
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: 0.8rem 1rem;
    font-size: 1rem;
  }
`;

const HamburgerButton = styled(motion.button)`
  display: none;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 44px;
  height: 44px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 50%;
  cursor: pointer;
  padding: 0;
  position: relative;
  z-index: 10;

  &:focus-visible {
    outline: 2px solid rgba(255, 255, 255, 0.8);
    outline-offset: 2px;
  }

  @media (max-width: 768px) {
    display: flex;
  }

  @media (max-width: 480px) {
    width: 40px;
    height: 40px;
  }

  span {
    width: 18px;
    height: 2px;
    background: rgba(255, 255, 255, 0.95);
    margin: 2px 0;
    transition: all 0.3s ease;
    transform-origin: center;
    border-radius: 2px;
  }

  &.open span:nth-child(1) {
    transform: rotate(45deg) translate(4px, 4px);
  }

  &.open span:nth-child(2) {
    opacity: 0;
    transform: translateX(6px);
  }

  &.open span:nth-child(3) {
    transform: rotate(-45deg) translate(4px, -4px);
  }
`;

const ProfileButton = styled(motion.button)`
  padding: 0.5rem 1.5rem;
  background: var(--button-primary, linear-gradient(135deg, #6495ED 0%, #4169E1 100%));
  color: white;
  border: none;
  border-radius: 25px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(100, 149, 237, 0.4);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    padding: 0.75rem 1.5rem;
  }
`;

const DashboardButton = styled(motion.button)`
  padding: 0.5rem 1.5rem;
  background: transparent;
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 25px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.5);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    padding: 0.75rem 1.5rem;
  }
`;

const ProfileIconButton = styled(motion.button)`
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: linear-gradient(145deg, #1e3c72 0%, #2a5298 50%, #7e8ba3 100%);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: white;
  font-size: 22px;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(20px);
  box-shadow: 
    0 8px 32px rgba(30, 60, 114, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.2),
    inset 0 -1px 0 rgba(0, 0, 0, 0.1);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, transparent 60%);
    opacity: 0;
    transition: opacity 0.4s ease;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 120%;
    height: 120%;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.25) 0%, transparent 50%);
    transform: translate(-50%, -50%) scale(0);
    transition: transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  
  &:hover {
    transform: scale(1.08) translateY(-3px);
    box-shadow: 
      0 12px 40px rgba(30, 60, 114, 0.4),
      0 4px 12px rgba(0, 0, 0, 0.15),
      inset 0 1px 0 rgba(255, 255, 255, 0.3);
    border-color: rgba(255, 255, 255, 0.25);
    background: linear-gradient(145deg, #2a5298 0%, #3d6db3 50%, #8e9bb3 100%);
    
    &::before {
      opacity: 1;
    }
    
    &::after {
      transform: translate(-50%, -50%) scale(1);
      opacity: 0;
    }
  }
  
  &:active {
    transform: scale(1.02) translateY(-1px);
    box-shadow: 
      0 6px 20px rgba(30, 60, 114, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.25);
  }
  
  @media (max-width: 768px) {
    width: 46px;
    height: 46px;
    font-size: 20px;
  }
  
  @media (max-width: 480px) {
    width: 42px;
    height: 42px;
    font-size: 18px;
  }
`;

const LogoutIconButton = styled(motion.button)`
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: linear-gradient(145deg, #c62828 0%, #d32f2f 50%, #e53935 100%);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: white;
  font-size: 22px;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(20px);
  box-shadow: 
    0 8px 32px rgba(198, 40, 40, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.2),
    inset 0 -1px 0 rgba(0, 0, 0, 0.1);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, transparent 60%);
    opacity: 0;
    transition: opacity 0.4s ease;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 120%;
    height: 120%;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.25) 0%, transparent 50%);
    transform: translate(-50%, -50%) scale(0);
    transition: transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  
  &:hover {
    transform: scale(1.08) translateY(-3px);
    box-shadow: 
      0 12px 40px rgba(198, 40, 40, 0.4),
      0 4px 12px rgba(0, 0, 0, 0.15),
      inset 0 1px 0 rgba(255, 255, 255, 0.3);
    border-color: rgba(255, 255, 255, 0.25);
    background: linear-gradient(145deg, #d32f2f 0%, #e53935 50%, #f44336 100%);
    
    &::before {
      opacity: 1;
    }
    
    &::after {
      transform: translate(-50%, -50%) scale(1);
      opacity: 0;
    }
  }
  
  &:active {
    transform: scale(1.02) translateY(-1px);
    box-shadow: 
      0 6px 20px rgba(198, 40, 40, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.25);
  }
  
  @media (max-width: 768px) {
    width: 46px;
    height: 46px;
    font-size: 20px;
  }
  
  @media (max-width: 480px) {
    width: 42px;
    height: 42px;
    font-size: 18px;
  }
`;

const Overlay = styled(motion.div)`
  display: none;
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
  background: rgba(2, 6, 23, 0.4);
  backdrop-filter: blur(4px);
  z-index: 999;

  @media (max-width: 768px) {
    display: block;
  }
`;

function Header({ onLoginClick, onSignupClick }) {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const checkLoginStatus = () => {
      const { token, user } = getAuthState();
      setIsLoggedIn(!!token && !!user && user.role === 'student');
    };

    const handleScroll = () => setIsScrolled(window.scrollY > 8);
    handleScroll();

    window.addEventListener('scroll', handleScroll, { passive: true });

    checkLoginStatus();

    const handleStorageChange = (e) => {
      if (!e || e.key === 'authToken' || e.key === 'user' || e.key === null) {
        checkLoginStatus();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('authstatechange', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('authstatechange', handleStorageChange);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [location.pathname]);

  const handleLogin = () => {
    onLoginClick();
    setIsMenuOpen(false);
  };

  const handleSignup = () => {
    onSignupClick();
    setIsMenuOpen(false);
  };

  const handleNavigation = (path) => {
    startTransition(() => {
      navigate(path);
      setIsMenuOpen(false);
    });
  };

  const handleHomeNavigation = () => {
    startTransition(() => {
      navigate('/');
      setIsMenuOpen(false);
    });
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    setIsLoggedIn(false);
    setIsMenuOpen(false);
    window.dispatchEvent(new Event('authstatechange'));
    window.location.href = '/';
  };

  const handleProfile = () => {
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    const user = localStorage.getItem('user') || sessionStorage.getItem('user');
    
    if (token && user) {
      // User is logged in, navigate to profile
      startTransition(() => {
        navigate('/profile');
      });
    } else {
      // User is not logged in, trigger login popup
      onLoginClick();
    }
    setIsMenuOpen(false);
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  // Check if current page is auth page to hide theme toggle in AuthPage but show in header
  const isAuthPage = location.pathname === '/login' || location.pathname === '/signup' || location.pathname === '/forgot-password';
  const shouldShowThemeToggle = !isAuthPage;
  const visibleNavItems = isLoggedIn
    ? [
        { label: 'Home', path: '/' },
        { label: 'About', path: '/about' },
        // { label: 'Advisory', path: '/advisory' },
        { label: 'Blogs', path: '/blogs' },
        { label: 'Latest Updates', path: '/latest-updates' },
        { label: 'Learning Resources', path: '/learning-resources' },
        { label: 'Contact', path: '/contact' },
      ]
    : [
        { label: 'Home', path: '/' },
        { label: 'About', path: '/about' },
        // { label: 'Advisory', path: '/advisory' },
        { label: 'Contact', path: '/contact' },
      ];

  return (
    <>
      <HeaderContainer $isScrolled={isScrolled}>
        <HeaderContent>
          <Logo
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleHomeNavigation}
          >
            <img src="/images/white_logo.svg" alt="JustPolymerSolutions" />
          </Logo>
          
          <Nav className={isMenuOpen ? 'open' : ''} aria-label="Primary navigation">
            {visibleNavItems.map((item) => (
              <NavLink
                key={item.path}
                $active={location.pathname === item.path || (item.path === '/' && location.pathname === '/')}
                whileHover={{ y: -2, scale: 1.01 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleNavigation(item.path)}
                type="button"
                aria-current={location.pathname === item.path ? 'page' : undefined}
              >
                {item.label}
              </NavLink>
            ))}

            <AuthButtons>
              {isLoggedIn ? (
                <>
                  <ProfileIconButton
                    onClick={handleProfile}
                    whileHover={{
                      scale: 1.08,
                      rotate: [0, 5, -5, 0],
                      transition: { duration: 0.3 }
                    }}
                    whileTap={{
                      scale: 0.95,
                      rotate: [0, -2, 0],
                      transition: { duration: 0.1 }
                    }}
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{
                      opacity: 1,
                      scale: 1,
                      transition: {
                        duration: 0.5,
                        delay: 0.2,
                        type: 'spring',
                        stiffness: 200,
                      }
                    }}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <defs>
                        <linearGradient id="profileGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stop-color="white" stop-opacity="0.9"/>
                          <stop offset="100%" stop-color="white" stop-opacity="0.7"/>
                        </linearGradient>
                      </defs>

                      <circle cx="12" cy="12" r="11.5" stroke="url(#profileGradient)" stroke-width="1" fill="none" opacity="0.6"/>
                      <circle cx="12" cy="12" r="9.5" stroke="url(#profileGradient)" stroke-width="0.5" fill="none" opacity="0.3"/>
                      <g fill="url(#profileGradient)">
                        <circle cx="12" cy="8" r="3.8"/>
                        <path d="M12 12.8C8.2 12.8 5.2 15.8 5.2 19.2H18.8C18.8 15.8 15.8 12.8 12 12.8Z"/>
                      </g>
                      <g stroke="url(#profileGradient)" stroke-width="1" fill="none" opacity="0.5">
                        <path d="M12 1L12 3" stroke-linecap="round"/>
                        <path d="M12 21L12 23" stroke-linecap="round"/>
                        <path d="M1 12L3 12" stroke-linecap="round"/>
                        <path d="M21 12L23 12" stroke-linecap="round"/>
                        <path d="M4.5 4.5L6 6" stroke-linecap="round"/>
                        <path d="M18 18L19.5 19.5" stroke-linecap="round"/>
                        <path d="M19.5 4.5L18 6" stroke-linecap="round"/>
                        <path d="M6 18L4.5 19.5" stroke-linecap="round"/>
                      </g>
                      <circle cx="12" cy="12" r="1.8" fill="url(#profileGradient)" opacity="0.7"/>
                      <circle cx="12" cy="12" r="0.8" fill="white" opacity="0.9"/>
                    </svg>
                  </ProfileIconButton>
                  <LogoutIconButton
                    onClick={handleLogout}
                    whileHover={{
                      scale: 1.08,
                      rotate: [0, 5, -5, 0],
                      transition: { duration: 0.3 }
                    }}
                    whileTap={{
                      scale: 0.95,
                      rotate: [0, -2, 0],
                      transition: { duration: 0.1 }
                    }}
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{
                      opacity: 1,
                      scale: 1,
                      transition: {
                        duration: 0.5,
                        delay: 0.3,
                        type: 'spring',
                        stiffness: 200,
                      }
                    }}
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <defs>
                        <linearGradient id="logoutGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stop-color="white" stop-opacity="0.95"/>
                          <stop offset="100%" stop-color="white" stop-opacity="0.8"/>
                        </linearGradient>
                      </defs>

                      <g stroke="url(#logoutGradient)" stroke-width="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17 17l5-5-5-5" opacity="1"/>
                        <line x1="22" y1="12" x2="10" y2="12" opacity="1"/>
                        <path d="M10 21H8a2 2 0 01-2-2V5a2 2 0 012-2h2" opacity="0.9"/>
                      </g>

                      <g stroke="url(#logoutGradient)" stroke-width="1" fill="none" opacity="0.6">
                        <path d="M19 9l3 3-3 3" stroke-linecap="round" stroke-linejoin="round"/>
                        <circle cx="7" cy="12" r="1" fill="url(#logoutGradient)" opacity="0.8"/>
                      </g>

                      <circle cx="12" cy="12" r="1.5" fill="url(#logoutGradient)" opacity="0.6"/>
                      <circle cx="12" cy="12" r="0.6" fill="white" opacity="1"/>
                    </svg>
                  </LogoutIconButton>
                </>
              ) : (
                <>
                  <LoginButton
                    onClick={handleLogin}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    type="button"
                  >
                    Login
                  </LoginButton>
                  <SignupButton
                    onClick={handleSignup}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    type="button"
                  >
                    Sign Up
                  </SignupButton>
                </>
              )}
              {shouldShowThemeToggle && <ThemeToggle />}
            </AuthButtons>
          </Nav>

          <HamburgerButton
            className={isMenuOpen ? 'open' : ''}
            onClick={toggleMenu}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            type="button"
            aria-label={isMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={isMenuOpen}
            aria-controls="mobile-nav"
          >
            <span></span>
            <span></span>
            <span></span>
          </HamburgerButton>
        </HeaderContent>
      </HeaderContainer>
      
      <AnimatePresence>
        {isMenuOpen && (
          <Overlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={closeMenu}
          />
        )}
      </AnimatePresence>
    </>
  );
}

export default Header;
